/**
 * The A–Z catalog is one static document for every filter: Gatsby serves the
 * same HTML for `?cat=immunity` as for the unfiltered catalog, so the browser
 * painted all of the tests — and the New tests rail — until React hydrated and
 * narrowed them down. That flash of the wrong page is what this removes.
 *
 * A tiny script copies the filter out of the URL onto `<html>` before the
 * directory markup is parsed, and the rules below hide everything the filter
 * excludes. Both the script and the rules leave the document at hydration,
 * when React owns the filtering again.
 */
import React from 'react';

import { IndexKind, LIVE_CATEGORIES } from '../../catalog';
import { C } from '../tokens';

export const CAT_ATTR = 'data-ewf-cat';
export const KIND_ATTR = 'data-ewf-kind';
export const QUERY_ATTR = 'data-ewf-q';

/** Keys are catalog data; only plain ones are safe inside a CSS selector. */
const SELECTOR_SAFE = /^[a-z0-9_-]+$/i;

const PREPAINT_CATEGORIES = LIVE_CATEGORIES.map((category) => category.key)
  .filter((key) => SELECTOR_SAFE.test(key))
  .sort();

export const countKey = (
  category: string | null,
  kind: IndexKind | null,
): string => {
  if (kind) return `kind:${kind}`;
  if (category) return `cat:${category}`;
  return 'all';
};

const CHIP_ON = `background: ${C.forest900}; box-shadow: none; color: #fff !important;`;
const CHIP_OFF = `background: #fff; box-shadow: inset 0 0 0 1px ${C.stroke}; color: ${C.ink900} !important;`;

const rule = (selectors: string[], declarations: string): string =>
  `${selectors.join(',\n')} { ${declarations} }`;

/** Any filter is pending; a filter the chip rail can show as chosen. */
const PENDING = [`html[${CAT_ATTR}]`, `html[${KIND_ATTR}]`, `html[${QUERY_ATTR}]`];
const CHOSEN = [`html[${CAT_ATTR}]`, `html[${KIND_ATTR}]`];

const filterRules = (
  on: string,
  {
    chip,
    count,
    excluded,
    included,
  }: { chip: string; count: string; excluded: string[]; included: string },
): string =>
  [
    rule(
      excluded.map((selector) => `${on} ${selector}`),
      'display: none;',
    ),
    rule([`${on} [data-az-group]:not(:has(${included}))`], 'display: none;'),
    rule([`${on} [data-count-for="${count}"]`], 'display: inline;'),
    rule([`${on} [data-chip="${chip}"]`], CHIP_ON),
  ].join('\n');

const RULES = [
  // Only the unfiltered summary shows until the script names a filter.
  rule(['[data-count-for]'], 'display: none;'),
  rule(['[data-count-for="all"]'], 'display: inline;'),
  rule(['[data-reset-pill]'], 'display: none;'),
  rule(
    PENDING.flatMap((on) => [
      `${on} [data-count-for="all"]`,
      `${on} [data-new-tests]`,
    ]),
    'display: none;',
  ),
  rule(
    CHOSEN.map((on) => `${on} [data-reset-pill]`),
    'display: inline-flex;',
  ),
  rule(
    PENDING.map((on) => `${on} [data-chip="all"]`),
    CHIP_OFF,
  ),
  // A text filter cannot be resolved without the catalog, so the directory
  // waits for React rather than showing rows the query excludes.
  rule([`html[${QUERY_ATTR}] [data-directory]`], 'visibility: hidden;'),
  ...PREPAINT_CATEGORIES.map((key) =>
    filterRules(`html[${CAT_ATTR}="${key}"]`, {
      chip: key,
      count: `cat:${key}`,
      excluded: [
        '[data-row-kind="kit"]',
        `[data-row-cat]:not([data-row-cat="${key}"])`,
      ],
      included: `[data-row-cat="${key}"]`,
    }),
  ),
  filterRules(`html[${KIND_ATTR}="panel"]`, {
    chip: 'panel',
    count: 'kind:panel',
    excluded: ['[data-row-kind="kit"]', '[data-row-panel="0"]'],
    included: '[data-row-panel="1"]',
  }),
  filterRules(`html[${KIND_ATTR}="kit"]`, {
    chip: 'kit',
    count: 'kind:kit',
    excluded: ['[data-row-kind="lab"]'],
    included: '[data-row-kind="kit"]',
  }),
].join('\n');

const SCRIPT = `(function(){try{
var p=new URLSearchParams(window.location.search),r=document.documentElement;
if((p.get('q')||'').trim())return r.setAttribute('${QUERY_ATTR}','');
var k=p.get('kind');
if(k==='panel'||k==='kit')return r.setAttribute('${KIND_ATTR}',k);
var c=p.get('cat');
if(c&&${JSON.stringify(PREPAINT_CATEGORIES)}.indexOf(c)>-1)r.setAttribute('${CAT_ATTR}',c);
}catch(e){}})();`;

/** Hands the filter back to React once it has painted the real thing. */
export function clearPrepaintFilter(): void {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  root.removeAttribute(CAT_ATTR);
  root.removeAttribute(KIND_ATTR);
  root.removeAttribute(QUERY_ATTR);
}

/**
 * Rendered above the catalog so the script runs before the rows are parsed.
 * Only present on the pre-hydration pass; React removes it on hydration.
 */
export function PrepaintFilter(): JSX.Element {
  return (
    <>
      <style
        data-ew-prepaint-style
        dangerouslySetInnerHTML={{ __html: RULES }}
      />
      <script
        data-ew-prepaint-script
        dangerouslySetInnerHTML={{ __html: SCRIPT }}
      />
    </>
  );
}
